import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  useActionDispositivo,
  useGetRegistriDispositivo,
} from "@/api";
import { SmallLoading } from "@/components/Loading";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import { isBancoDiFlussaggioPortataInRange } from "@/components/DispositivoDetails/maschere/registri/BANCO_DI_FLUSSAGGIO";
import { Alert, Box, Grid, Stack, Switch, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toBool } from "../../utils";

export type BANCO_DI_FLUSSAGGIO_Action =
  | "start p fluss1"
  | "start p fluss2"
  | "stop p fluss1"
  | "stop p fluss2"
  | "start p ricircolo"
  | "stop p ricircolo"
  | "reset"
  | "start p contapar"
  | "stop p contapar"
  | "start resistenza 1"
  | "start resistenza 2"
  | "start resistenza 3"
  | "start resistenza 4"
  | "start resistenza 5"
  | "start resistenza 6"
  | "stop resistenza 1"
  | "stop resistenza 2"
  | "stop resistenza 3"
  | "stop resistenza 4"
  | "stop resistenza 5"
  | "stop resistenza 6"
  | "start auto"
  | "stop auto";

export function BANCO_DI_FLUSSAGGIO_Buttons({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const [sentAction, setSentAction] =
    useState<BANCO_DI_FLUSSAGGIO_Action | null>(null);

  const { data: registri } = useGetRegistriDispositivo(nodo.id);

  const { mutateAsync: setActionAsync, isLoading: actionLoading } =
    useActionDispositivo();

  const queryClient = useQueryClient();

  async function setAction(action: BANCO_DI_FLUSSAGGIO_Action) {
    try {
      setSentAction(action);
      const response = await setActionAsync({
        id: nodo.id,
        data: { action },
      });
      if (response.data?.updated) {
        toast.success(t("dispositivoActions.toast.success"));
      } else {
        toast.warn(t("dispositivoActions.toast.noResponse"));
      }
    } catch {
      toast.error(t("dispositivoActions.toast.error"));
    } finally {
      setSentAction(null);
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
    }
  }

  const info = nodo.dispositivoLastInfo ?? {};

  const statoRicircolo = toBool(info.statopric);
  const statoFluss1 = toBool(info.statopflu1);
  const statoFluss2 = toBool(info.statopflu2);
  const statoContapar = toBool(info.statopcpar);

  const status = (nodo.dispositivoLastStatus ?? "").trim().toLowerCase();
  const autoRunning = status === "on";

  const portataInRange = isBancoDiFlussaggioPortataInRange(
    registri?.data ?? []
  );
  const portataOutOfRange = !portataInRange;

  const controlsLocked = actionLoading;

  const canReset = status === "alarm";

  const canStartAuto = status === "stop" && !portataOutOfRange;
  const canStopAuto = autoRunning;

  // per entrambi i banchi di flussaggio in manuale possono andare contemporaneamente le pompe a discrezione dell’operatore,
  //  l’unica che ha una “regola” anche in manuale è la pompa contaparticelle che può partire solo se c’è almeno un flussaggio acceso.
  const funzionamentoManuale =
    status === "on-manuale" || status === "stop-manuale";
  const canTogglePompaFluss1 = funzionamentoManuale;
  const canTogglePompaFluss2 = funzionamentoManuale;
  const canToggleRic = funzionamentoManuale;
  const canToggleCont = statoContapar || statoFluss1 || statoFluss2;

  const resistenzaState = useCallback(
    (index: number) => {
      const tag = `resigrp${index}`;
      return toBool(info[tag]);
    },
    [info]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartAuto}
            onClick={() => setAction("start auto")}
            startIcon={
              sentAction === "start auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start auto
          </Button>
        </Grid>

        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopAuto}
            onClick={() => setAction("stop auto")}
            startIcon={
              sentAction === "stop auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop auto
          </Button>
        </Grid>
        <Grid item xs={12}>
          {portataOutOfRange && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Calcolo portata fuori range (10-1000 L/MIN)
            </Alert>
          )}
        </Grid>

        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canReset}
            onClick={() => setAction("reset")}
            startIcon={
              sentAction === "reset" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("azioni.reset")}
          </Button>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 0.5,
            width: "250px",
            zoom: 0.7,
          }}
        >
          <Typography
            variant="button"
            fontWeight="medium"
            color="text"
            display="block"
            mb={1}
          >
            {t("bancoFlussaggio.pumps")}
          </Typography>
          <Stack spacing={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="button" fontWeight="regular" color="text">
                {t("bancoFlussaggio.pumpFlussaggio1")}
              </Typography>
              <Switch
                checked={statoFluss1}
                disabled={controlsLocked || !canTogglePompaFluss1}
                onChange={(event) =>
                  setAction(
                    event.target.checked ? "start p fluss1" : "stop p fluss1"
                  )
                }
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="button" fontWeight="regular" color="text">
                {t("bancoFlussaggio.pumpFlussaggio2")}
              </Typography>
              <Switch
                checked={statoFluss2}
                disabled={controlsLocked || !canTogglePompaFluss2}
                onChange={(event) =>
                  setAction(
                    event.target.checked ? "start p fluss2" : "stop p fluss2"
                  )
                }
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="button" fontWeight="regular" color="text">
                {t("bancoFlussaggio.pumpRicircolo")}
              </Typography>
              <Switch
                checked={statoRicircolo}
                disabled={controlsLocked || !canToggleRic}
                onChange={(event) =>
                  setAction(
                    event.target.checked
                      ? "start p ricircolo"
                      : "stop p ricircolo"
                  )
                }
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="button" fontWeight="regular" color="text">
                {t("bancoFlussaggio.pumpContaparticelle")}
              </Typography>
              <Switch
                checked={statoContapar}
                disabled={controlsLocked || !canToggleCont}
                onChange={(event) =>
                  setAction(
                    event.target.checked
                      ? "start p contapar"
                      : "stop p contapar"
                  )
                }
              />
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 0.5,
            width: "250px",
            zoom: 0.7,
          }}
        >
          <Typography
            variant="button"
            fontWeight="medium"
            color="text"
            display="block"
            mb={1}
          >
            {t("bancoFlussaggio.resistances")}
          </Typography>
          <Stack spacing={0.5}>
            {[1, 2, 3, 4, 5, 6].map((index) => {
              const isOn = resistenzaState(index);
              const startAction =
                `start resistenza ${index}` as BANCO_DI_FLUSSAGGIO_Action;
              const stopAction =
                `stop resistenza ${index}` as BANCO_DI_FLUSSAGGIO_Action;

              return (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    {t("bancoFlussaggio.resistance", { index })}
                  </Typography>
                  <Switch
                    checked={isOn}
                    disabled={controlsLocked}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setAction(startAction);
                        return;
                      }
                      setAction(stopAction);
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
